/**
 * Filter Modal V2 - Compact Grouped Layout
 * Mockup for improved UX with collapsible sections
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  CheckCircle,
  XCircle,
  Archive,
  Ban,
  BookOpen,
  Users,
  AlertTriangle,
  X,
  Filter as FilterIcon,
  Loader2,
  Mail,
  RefreshCcw,
  Coins,
  CircleOff,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Wallet,
  UserCheck,
  FileWarning,
  StickyNote,
  Flag
} from 'lucide-react';

// Collapsible Section Component
function FilterSection({ title, icon: Icon, children, defaultOpen = true, badge = null }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{title}</span>
          {badge && (
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs h-5">
              {badge}
            </Badge>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-3 space-y-3 bg-background">
          {children}
        </div>
      )}
    </div>
  );
}

// Compact Filter Row Component
function FilterRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2">
      <div className="text-sm font-medium text-muted-foreground w-24 shrink-0 pt-1">
        {label}
      </div>
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
}

// Inline Radio Option Component
function InlineRadioOption({ value, id, label, icon: Icon, iconColor, checked, onChange }) {
  return (
    <label
      htmlFor={id}
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer text-sm
        border transition-colors
        ${checked
          ? 'bg-primary/10 border-primary text-primary'
          : 'bg-background border-transparent hover:bg-muted/50'
        }
      `}
    >
      <input
        type="radio"
        id={id}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      {Icon && <Icon className={`h-3.5 w-3.5 ${iconColor || ''}`} />}
      {label}
    </label>
  );
}

export function FilterModalV2({
  open,
  onOpenChange,
  statusFilter,
  setStatusFilter,
  courseFilter,
  setCourseFilter,
  cinnireFilter,
  setCinnireFilter,
  hasFriendsFilter,
  setHasFriendsFilter,
  notesFilter,
  setNotesFilter,
  uploadStatusFilter,
  setUploadStatusFilter,
  campaignSentFilter,
  setCampaignSentFilter,
  interviewStageFilter,
  setInterviewStageFilter,
  paymentStageFilter,
  setPaymentStageFilter,
  courses,
  coursesLoading,
  activeFilterCount,
  onClearAll,
  onApply,
  showUploadStatusFilter = true,
  showCampaignSentFilter = false,
  showInterviewStageFilter = false,
  showPaymentStageFilter = false,
  campaignName = ''
}) {
  const { t } = useTranslation();

  const handleApply = () => {
    onApply?.();
    onOpenChange(false);
  };

  // Count filters per section for badges
  const studentFilterCount = (statusFilter && statusFilter !== 'ACTIVE' ? 1 : 0) + (cinnireFilter ? 1 : 0) + (hasFriendsFilter ? 1 : 0) + (notesFilter ? 1 : 0);
  const academicFilterCount = (courseFilter ? 1 : 0) + (interviewStageFilter ? 1 : 0);
  const financialFilterCount = paymentStageFilter ? 1 : 0;
  const dataQualityFilterCount = uploadStatusFilter ? 1 : 0;
  const campaignFilterCount = campaignSentFilter ? 1 : 0;

  // Archived courses only make sense to pick when actually looking for archived
  // students — otherwise they'd clutter this list with old/duplicate-looking entries
  // once a course name recurs across sessions.
  const courseOptions = (courses || []).filter(
    (course) => statusFilter === 'ARCHIVED' || !course.has_archived_students
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FilterIcon className="h-5 w-5" />
              {t('filters.title', 'Filter Students')}
            </DialogTitle>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                {t('filters.clearAll', 'Clear all')} ({activeFilterCount})
              </Button>
            )}
          </div>
          <DialogDescription className="text-sm">
            {t('filters.description', 'Refine your list with multiple criteria')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {/* Student Section: Status + Role */}
          <FilterSection
            title={t('filters.section.student', 'Student')}
            icon={UserCheck}
            badge={studentFilterCount > 0 ? studentFilterCount : null}
          >
            {/* Status */}
            <FilterRow label={t('filters.status', 'Status')}>
              <div className="flex flex-wrap gap-1.5">
                <InlineRadioOption
                  value="ACTIVE"
                  id="v2-status-active"
                  label={t('status.ACTIVE', 'Active')}
                  icon={CheckCircle}
                  iconColor="text-green-600"
                  checked={statusFilter === 'ACTIVE'}
                  onChange={setStatusFilter}
                />
                <InlineRadioOption
                  value="COMPLETED"
                  id="v2-status-completed"
                  label={t('status.COMPLETED', 'Completed')}
                  icon={CheckCircle}
                  iconColor="text-blue-600"
                  checked={statusFilter === 'COMPLETED'}
                  onChange={setStatusFilter}
                />
                <InlineRadioOption
                  value="ARCHIVED"
                  id="v2-status-archived"
                  label={t('status.ARCHIVED', 'Archived')}
                  icon={Archive}
                  iconColor="text-gray-600"
                  checked={statusFilter === 'ARCHIVED'}
                  onChange={setStatusFilter}
                />
                <InlineRadioOption
                  value="CANCELLED"
                  id="v2-status-cancelled"
                  label={t('status.CANCELLED', 'Cancelled')}
                  icon={Ban}
                  iconColor="text-red-600"
                  checked={statusFilter === 'CANCELLED'}
                  onChange={setStatusFilter}
                />
                <InlineRadioOption
                  value=""
                  id="v2-status-all"
                  label={t('filters.allStatuses', 'All')}
                  checked={statusFilter === ''}
                  onChange={setStatusFilter}
                />
              </div>
            </FilterRow>

            <Separator className="my-2" />

            {/* Role (Cinnire) */}
            <FilterRow label={t('filters.role', 'Role')}>
              <div className="flex flex-wrap gap-1.5">
                <InlineRadioOption
                  value=""
                  id="v2-role-all"
                  label={t('filters.allStudents', 'All')}
                  checked={cinnireFilter === ''}
                  onChange={setCinnireFilter}
                />
                <InlineRadioOption
                  value="true"
                  id="v2-role-cinniri"
                  label={t('filters.cinniriOnly', 'Cinniri Only')}
                  icon={Users}
                  iconColor="text-purple-600"
                  checked={cinnireFilter === 'true'}
                  onChange={setCinnireFilter}
                />
                <InlineRadioOption
                  value="false"
                  id="v2-role-non-cinniri"
                  label={t('filters.nonCinniri', 'Non-Cinniri')}
                  checked={cinnireFilter === 'false'}
                  onChange={setCinnireFilter}
                />
              </div>
            </FilterRow>

            <Separator className="my-2" />

            {/* Friends */}
            <FilterRow label={t('filters.friends', 'Friends')}>
              <div className="flex flex-wrap gap-1.5">
                <InlineRadioOption
                  value=""
                  id="v2-friends-all"
                  label={t('filters.allStudents', 'All')}
                  checked={hasFriendsFilter === ''}
                  onChange={setHasFriendsFilter}
                />
                <InlineRadioOption
                  value="true"
                  id="v2-friends-yes"
                  label={t('filters.hasFriends', 'Has Friends')}
                  icon={Users}
                  iconColor="text-blue-600"
                  checked={hasFriendsFilter === 'true'}
                  onChange={setHasFriendsFilter}
                />
                <InlineRadioOption
                  value="false"
                  id="v2-friends-no"
                  label={t('filters.noFriends', 'No Friends')}
                  icon={CircleOff}
                  iconColor="text-gray-600"
                  checked={hasFriendsFilter === 'false'}
                  onChange={setHasFriendsFilter}
                />
              </div>
            </FilterRow>

            <Separator className="my-2" />

            {/* Notes */}
            <FilterRow label={t('filters.notes.label', 'Notes')}>
              <div className="flex flex-wrap gap-1.5">
                <InlineRadioOption
                  value=""
                  id="v2-notes-all"
                  label={t('filters.allStudents', 'All')}
                  checked={notesFilter === ''}
                  onChange={setNotesFilter}
                />
                <InlineRadioOption
                  value="true"
                  id="v2-notes-yes"
                  label={t('filters.notes.hasNotes', 'Has Notes')}
                  icon={StickyNote}
                  iconColor="text-amber-500"
                  checked={notesFilter === 'true'}
                  onChange={setNotesFilter}
                />
                <InlineRadioOption
                  value="false"
                  id="v2-notes-no"
                  label={t('filters.notes.noNotes', 'No Notes')}
                  icon={CircleOff}
                  iconColor="text-gray-600"
                  checked={notesFilter === 'false'}
                  onChange={setNotesFilter}
                />
              </div>
            </FilterRow>
          </FilterSection>

          {/* Academic Section: Course + Interview */}
          <FilterSection
            title={t('filters.section.academic', 'Academic')}
            icon={GraduationCap}
            badge={academicFilterCount > 0 ? academicFilterCount : null}
          >
            {/* Course */}
            <FilterRow label={t('filters.course', 'Course')}>
              {coursesLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading', 'Loading...')}
                </div>
              ) : courseOptions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  <InlineRadioOption
                    value=""
                    id="v2-course-all"
                    label={t('filters.allCourses', 'All')}
                    checked={courseFilter === ''}
                    onChange={setCourseFilter}
                  />
                  {courseOptions.map(course => (
                    <InlineRadioOption
                      key={course.uuid}
                      value={course.uuid}
                      id={`v2-course-${course.uuid}`}
                      label={course.has_archived_students
                        ? `${course.name} (${t('courses.archive.archivedBadge', 'Archived')})`
                        : course.name}
                      icon={BookOpen}
                      iconColor="text-blue-600"
                      checked={courseFilter === course.uuid}
                      onChange={setCourseFilter}
                    />
                  ))}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t('filters.noCourses', 'No courses available')}
                </span>
              )}
            </FilterRow>

            {/* Interview Stage - Conditionally shown */}
            {showInterviewStageFilter && (
              <>
                <Separator className="my-2" />
                <FilterRow label={t('filters.interview.label', 'Interview')}>
                  <div className="flex flex-wrap gap-1.5">
                    <InlineRadioOption
                      value=""
                      id="v2-interview-all"
                      label={t('filters.interview.all', 'All')}
                      checked={interviewStageFilter === ''}
                      onChange={setInterviewStageFilter}
                    />
                    <InlineRadioOption
                      value="scored"
                      id="v2-interview-scored"
                      label={t('filters.interview.scored', 'Scored')}
                      icon={CheckCircle}
                      iconColor="text-green-600"
                      checked={interviewStageFilter === 'scored'}
                      onChange={setInterviewStageFilter}
                    />
                    <InlineRadioOption
                      value="not_scored"
                      id="v2-interview-not-scored"
                      label={t('filters.interview.notScored', 'Not Scored')}
                      icon={XCircle}
                      iconColor="text-gray-500"
                      checked={interviewStageFilter === 'not_scored'}
                      onChange={setInterviewStageFilter}
                    />
                    {/* Individual scores */}
                    {[1, 2, 3, 4, 5].map(score => (
                      <InlineRadioOption
                        key={score}
                        value={`score_${score}`}
                        id={`v2-interview-score-${score}`}
                        label={`${score}`}
                        checked={interviewStageFilter === `score_${score}`}
                        onChange={setInterviewStageFilter}
                      />
                    ))}
                  </div>
                </FilterRow>
              </>
            )}
          </FilterSection>

          {/* Financial Section: Payment Stage */}
          {showPaymentStageFilter && (
            <FilterSection
              title={t('filters.section.financial', 'Financial')}
              icon={Wallet}
              badge={financialFilterCount > 0 ? financialFilterCount : null}
            >
              <FilterRow label={t('filters.payment.label', 'Payment')}>
                <div className="flex flex-wrap gap-1.5">
                  <InlineRadioOption
                    value=""
                    id="v2-payment-all"
                    label={t('filters.payment.all', 'All')}
                    checked={paymentStageFilter === ''}
                    onChange={setPaymentStageFilter}
                  />
                  <InlineRadioOption
                    value="fully_paid"
                    id="v2-payment-fully-paid"
                    label={t('filters.payment.fullyPaid', 'Fully Paid')}
                    icon={CheckCircle}
                    iconColor="text-green-600"
                    checked={paymentStageFilter === 'fully_paid'}
                    onChange={setPaymentStageFilter}
                  />
                  <InlineRadioOption
                    value="outstanding"
                    id="v2-payment-outstanding"
                    label={t('filters.payment.outstanding', 'Outstanding')}
                    icon={AlertTriangle}
                    iconColor="text-yellow-600"
                    checked={paymentStageFilter === 'outstanding'}
                    onChange={setPaymentStageFilter}
                  />
                  <InlineRadioOption
                    value="deposit_only"
                    id="v2-payment-deposit-only"
                    label={t('filters.payment.depositOnly', 'Deposit Only')}
                    icon={Coins}
                    iconColor="text-amber-600"
                    checked={paymentStageFilter === 'deposit_only'}
                    onChange={setPaymentStageFilter}
                  />
                  <InlineRadioOption
                    value="no_payments"
                    id="v2-payment-no-payments"
                    label={t('filters.payment.noPayments', 'No Payments')}
                    icon={CircleOff}
                    iconColor="text-gray-500"
                    checked={paymentStageFilter === 'no_payments'}
                    onChange={setPaymentStageFilter}
                  />
                  <InlineRadioOption
                    value="refunded"
                    id="v2-payment-refunded"
                    label={t('filters.payment.refunded', 'Refunded')}
                    icon={RefreshCcw}
                    iconColor="text-blue-600"
                    checked={paymentStageFilter === 'refunded'}
                    onChange={setPaymentStageFilter}
                  />
                </div>
              </FilterRow>
            </FilterSection>
          )}

          {/* Data Quality Section: Import Issues */}
          {showUploadStatusFilter && (
            <FilterSection
              title={t('filters.section.dataQuality', 'Data Quality')}
              icon={FileWarning}
              defaultOpen={false}
              badge={dataQualityFilterCount > 0 ? dataQualityFilterCount : null}
            >
              <FilterRow label={t('filters.import', 'Import')}>
                <div className="flex flex-wrap gap-1.5">
                  <InlineRadioOption
                    value=""
                    id="v2-upload-none"
                    label={t('filters.noFilter', 'All')}
                    checked={uploadStatusFilter === ''}
                    onChange={setUploadStatusFilter}
                  />
                  <InlineRadioOption
                    value="HAS_ISSUES"
                    id="v2-upload-both"
                    label={t('filters.import.withIssues', 'With Issues')}
                    icon={AlertTriangle}
                    iconColor="text-orange-600"
                    checked={uploadStatusFilter === 'HAS_ISSUES'}
                    onChange={setUploadStatusFilter}
                  />
                  <InlineRadioOption
                    value="IMPORT_WARNING"
                    id="v2-upload-warning"
                    label={t('filters.import.warnings', 'Warnings')}
                    icon={AlertTriangle}
                    iconColor="text-yellow-600"
                    checked={uploadStatusFilter === 'IMPORT_WARNING'}
                    onChange={setUploadStatusFilter}
                  />
                  <InlineRadioOption
                    value="IMPORT_ERROR"
                    id="v2-upload-error"
                    label={t('filters.import.errors', 'Errors')}
                    icon={XCircle}
                    iconColor="text-red-600"
                    checked={uploadStatusFilter === 'IMPORT_ERROR'}
                    onChange={setUploadStatusFilter}
                  />
                  <InlineRadioOption
                    value="ACTION_REQUIRED"
                    id="v2-upload-action-required"
                    label={t('filters.import.actionRequired', 'Action Required')}
                    icon={Flag}
                    iconColor="text-blue-600"
                    checked={uploadStatusFilter === 'ACTION_REQUIRED'}
                    onChange={setUploadStatusFilter}
                  />
                </div>
              </FilterRow>
            </FilterSection>
          )}

          {/* Campaign Section: Email Status */}
          {showCampaignSentFilter && campaignName && (
            <FilterSection
              title={t('filters.section.campaign', 'Campaign')}
              icon={Mail}
              badge={campaignFilterCount > 0 ? campaignFilterCount : null}
            >
              <FilterRow label={t('filters.emailStatus', 'Email')}>
                <div className="flex flex-wrap gap-1.5">
                  <InlineRadioOption
                    value=""
                    id="v2-sent-all"
                    label={t('filters.allStudents', 'All')}
                    checked={campaignSentFilter === ''}
                    onChange={setCampaignSentFilter}
                  />
                  <InlineRadioOption
                    value="sent"
                    id="v2-sent-sent"
                    label={t('filters.campaign.sent', 'Already Sent')}
                    icon={CheckCircle}
                    iconColor="text-green-600"
                    checked={campaignSentFilter === 'sent'}
                    onChange={setCampaignSentFilter}
                  />
                  <InlineRadioOption
                    value="not_sent"
                    id="v2-sent-not-sent"
                    label={t('filters.campaign.notSent', 'Not Sent')}
                    icon={XCircle}
                    iconColor="text-gray-600"
                    checked={campaignSentFilter === 'not_sent'}
                    onChange={setCampaignSentFilter}
                  />
                </div>
              </FilterRow>
            </FilterSection>
          )}
        </div>

        <DialogFooter className="pt-4 border-t">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {activeFilterCount > 0 && (
                <span>
                  {activeFilterCount} {activeFilterCount === 1 ? t('filters.filterActive', 'filter active') : t('filters.filtersActive', 'filters active')}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleApply}>
                <FilterIcon className="h-4 w-4 mr-2" />
                {t('filters.apply', 'Apply Filters')}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FilterModalV2;
